import { computed, inject, Injectable } from '@angular/core';

import { CurrentSiteState } from '@ppg/configuration';
import { ClientSiteSettingsKeys, ResourceItem } from '@ppg/core/enums';
import { ResourceService } from '@ppg/shared/resource';

import { FederatedSearchService } from './federated-search.service';
import {
  COLOR_SOURCE_ID,
  CONTENT_SOURCE_ID,
  DOCUMENT_SOURCE_ID,
  PRODUCT_SOURCE_ID,
  SourceIdType,
} from '../../consts/federated-search.const';
import { PluginTypes } from '../../enums/plugin-type.enum';
import { PluginSettings } from '../../interfaces/plugin-settings.interface';

@Injectable({ providedIn: 'root' })
export class FederatedSearchPluginsService {
  readonly #federatedSearchService = inject(FederatedSearchService);
  readonly #currentSiteState = inject(CurrentSiteState);
  readonly #resourceService = inject(ResourceService);

  readonly #contentIndex = this.#federatedSearchService.contentIndex;
  readonly #productIndex = this.#federatedSearchService.productIndex;
  readonly #colorIndex = this.#federatedSearchService.colorIndex;
  readonly #documentIndex = this.#federatedSearchService.documentIndex;
  readonly #searchClient = this.#federatedSearchService.searchClient;
  readonly #hitsPerPage = this.#federatedSearchService.hitsPerPage;
  readonly #contentFilters = this.#federatedSearchService.contentFilters;

  readonly #federatedSearchColumnsOrder = computed(() =>
    this.#currentSiteState.getSiteSettingValueByKey(ClientSiteSettingsKeys.FederatedSearchColumnsOrder),
  );

  readonly #splittedFederatedSearchColumnsOrder = computed(() =>
    this.#federatedSearchColumnsOrder()
      .split(';')
      .filter((_) => _),
  );

  readonly #pluginSettingsMap: Record<PluginTypes, () => PluginSettings | null> = {
    [PluginTypes.Content]: computed(() =>
      this.createPluginSettings(
        this.#contentIndex(),
        CONTENT_SOURCE_ID,
        this.pluginsTextResources().all,
        this.#contentFilters(),
      ),
    ),
    [PluginTypes.Products]: computed(() =>
      this.createPluginSettings(this.#productIndex(), PRODUCT_SOURCE_ID, this.pluginsTextResources().products),
    ),
    [PluginTypes.Colors]: computed(() =>
      this.createPluginSettings(this.#colorIndex(), COLOR_SOURCE_ID, this.pluginsTextResources().colors),
    ),
    [PluginTypes.Documents]: computed(() =>
      this.createPluginSettings(this.#documentIndex(), DOCUMENT_SOURCE_ID, this.pluginsTextResources().documents),
    ),
  };

  readonly #pluginsInDefaultOrder = computed(() => [
    PluginTypes.Content,
    PluginTypes.Products,
    PluginTypes.Colors,
    PluginTypes.Documents,
  ]);

  public readonly pluginsTextResources = computed(() => ({
    products: this.#resourceService.getResourceByKey(ResourceItem.FEDERATED_SEARCH_PRODUCTS),
    documents: this.#resourceService.getResourceByKey(ResourceItem.FEDERATED_SEARCH_DOCUMENTS),
    colors: this.#resourceService.getResourceByKey(ResourceItem.FEDERATED_SEARCH_COLORS),
    all: this.#resourceService.getResourceByKey(ResourceItem.FEDERATED_SEARCH_ALL),
  }));

  public readonly pluginsSettings = computed(() => {
    const columnsOrder = this.#splittedFederatedSearchColumnsOrder();
    const areValidColumnsNames = columnsOrder.every((item) => Object.values(PluginTypes).includes(item as PluginTypes));
    const hasDuplicates = new Set(columnsOrder).size !== columnsOrder.length;

    const defaultPlugins = this.mapOrderToPluginSettings(this.#pluginsInDefaultOrder());

    if (!areValidColumnsNames || hasDuplicates) {
      return defaultPlugins;
    }

    const isCorrectOrderLength = columnsOrder.length >= defaultPlugins.length;

    if (!isCorrectOrderLength) {
      return defaultPlugins;
    }

    return this.mapOrderToPluginSettings(columnsOrder);
  });

  private mapOrderToPluginSettings(columnsOrder: string[]): PluginSettings[] {
    return columnsOrder
      .map((column) => this.#pluginSettingsMap[column as PluginTypes]?.())
      .filter((plugin): plugin is PluginSettings => !!plugin);
  }

  private createPluginSettings(
    index: string | null,
    sourceId: SourceIdType,
    header: string,
    facetFilters?: (string | string[])[],
  ): PluginSettings | null {
    if (!index) return null;

    return {
      searchClient: this.#searchClient,
      sourceId,
      indexName: index,
      hitsPerPage: this.#hitsPerPage(),
      header,
      ...(facetFilters ? { facetFilters } : {}),
    };
  }
}
